import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Archive, Copy } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button } from '../../components/ui/button'
import { ConfirmDialog } from '../../components/ui/confirm-dialog'
import { errorCode, errorReason, sessionGone } from '../../lib/errors'
import { Problem } from '../components/chrome'
import { PasskeyNote } from '../components/passkey-note'
import { useReauth } from '../components/reauth'
import { useI18n } from '../lib/i18n'
import type { CatalogCourse } from '../lib/screens'
import { useTRPC, useTRPCClient } from '../lib/trpc'

/*
 * «Duplicar» and «Archivar» on a card of «Cursos» (ADR 0009 §8; PRD 04 §2.4; Figma 86:2693, where
 * «Eliminar» becomes «Archivar»). Duplicating asks first what it does and then follows the job
 * that makes the copy in the background, up to a link to its editor; it needs no passkey (it
 * creates a draft, as «Crear nuevo curso»). Archiving asks the server what it would do (how many
 * companies and people have the course, counts only, and whether a draft open in the editor would
 * be discarded), says so in a confirmation, and then needs the passkey again, as the other
 * sensitive actions. Both through the query client's cache, whose errors end a session the server
 * no longer knows. Marked for design review.
 */

/** What a card says once an action is done, or why it could not be. */
export interface CardNotice {
  tone: 'done' | 'error'
  text: string
}

/** The texts of a refusal that is neither about the course nor about the session. */
function refusalText(t: ReturnType<typeof useI18n>['t'], err: unknown): string {
  return errorCode(err) === 'TOO_MANY_REQUESTS'
    ? t('common.error.rateLimited')
    : t('common.error.generic')
}

/** How often the page asks where a duplicate stands. */
export const DUPLICATE_POLL_MS = 2000

/**
 * «Duplicar»: a confirmation that says what the duplicate takes (the published version's content,
 * or the draft's for a course never published) and what it does not (series, level, companies).
 * The call answers at once with the job (`onStarted`), which the card follows; a refusal stays in
 * the confirmation. While the card's job runs, the button says so and waits.
 */
export function DuplicateAction({
  course,
  title,
  running,
  onStarted,
}: {
  course: CatalogCourse
  title: string
  running: boolean
  onStarted: (jobId: string) => void
}) {
  const { t } = useI18n()
  const client = useTRPCClient()
  const button = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const duplicate = useMutation({
    mutationFn: () => client.catalog.duplicate.mutate({ courseId: course.id }),
    gcTime: 0,
    onSuccess: ({ jobId }) => {
      setOpen(false)
      onStarted(jobId)
    },
    onError: (err) => {
      if (sessionGone(err)) return
      setError(
        errorReason(err) === 'not-found' ? t('catalog.duplicate.unavailable') : refusalText(t, err),
      )
    },
  })
  return (
    <>
      <Button
        ref={button}
        variant="secondary"
        size="sm"
        aria-label={t('catalog.duplicate.label', { course: title })}
        aria-disabled={running || undefined}
        className="aria-disabled:cursor-progress aria-disabled:opacity-50"
        onClick={() => {
          if (running) return
          setError(null)
          setOpen(true)
        }}
      >
        <Copy aria-hidden className="size-4" />
        {running ? t('catalog.duplicate.busy') : t('catalog.duplicate')}
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={t('catalog.duplicate.title', { course: title })}
        confirmLabel={t('catalog.duplicate')}
        busyLabel={t('catalog.duplicate.busy')}
        cancelLabel={t('common.cancel')}
        busy={duplicate.isPending}
        error={error}
        onConfirm={() => {
          setError(null)
          duplicate.mutate()
        }}
        opener={button}
      >
        <p>
          {t(
            course.status === 'draft'
              ? 'catalog.duplicate.body.draft'
              : 'catalog.duplicate.body.published',
          )}
        </p>
        <p>{t('catalog.duplicate.after')}</p>
      </ConfirmDialog>
    </>
  )
}

/** Where the duplicate a card started stands, as its card shows it. */
type Duplication =
  | { status: 'none' | 'running' | 'unknown' }
  | { status: 'done'; courseId: string }
  | { status: 'failed'; code: string }

/**
 * Follows a duplicate: the page asks every few seconds while it runs, also with its tab hidden, and
 * each time counts as the session's activity, which the job's write needs (ADR 0009 §8). An answer
 * that fails does not stop it, unless the job is not there: then the page does not know how it
 * ended. Done, the catalogue is asked again, with the new draft.
 */
export function useDuplication(jobId: string | null): Duplication {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const job = useQuery({
    ...trpc.catalog.duplicateStatus.queryOptions({ jobId: jobId ?? '' }),
    enabled: jobId !== null,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: true,
    refetchInterval: ({ state }) => {
      if (state.data) return state.data.status === 'running' ? DUPLICATE_POLL_MS : false
      return errorReason(state.error) === 'not-found' ? false : DUPLICATE_POLL_MS
    },
    retry: false,
  })
  const done = job.data?.status === 'done'
  useEffect(() => {
    if (done) void queryClient.invalidateQueries({ queryKey: trpc.catalog.list.queryKey() })
  }, [done, queryClient, trpc])
  if (jobId === null) return { status: 'none' }
  if (job.data) return job.data
  return { status: errorReason(job.error) === 'not-found' ? 'unknown' : 'running' }
}

/**
 * What a card says of its duplicate: that it runs; done, a link to the new draft's editor; failed,
 * its code, never a text of the server. Both lines are always on the card, so that a screen reader
 * reads them out as they change. Marked for design review.
 */
export function DuplicationStatus({ duplication }: { duplication: Duplication }) {
  const { t } = useI18n()
  return (
    <>
      <p role="status" className="text-sm not-empty:mt-3">
        {duplication.status === 'running' && t('catalog.duplicate.running')}
        {duplication.status === 'done' && (
          <>
            {t('catalog.duplicate.done')}{' '}
            <Link
              to="/cursos/$courseId/editar"
              params={{ courseId: duplication.courseId }}
              className="underline"
            >
              {t('catalog.duplicate.open')}
            </Link>
          </>
        )}
      </p>
      <Problem>
        {duplication.status === 'failed' &&
          t('catalog.duplicate.failed', { code: duplication.code })}
        {duplication.status === 'unknown' && t('catalog.duplicate.unknown')}
      </Problem>
    </>
  )
}

/** The draft open in the editor that archiving would discard, as the server named it. */
interface SeenDraft {
  draftKey: string
  revision: number
}

/**
 * «Archivar» a published course: what it would do (`catalog.archiveCheck`, a view of the course in
 * Eklevo's trail), a confirmation that says it, the passkey again if it was not touched in the last
 * minutes (once the confirmation has closed and the focus is back on the button), and the call,
 * with the draft the confirmation spoke of (or none). If a draft was opened or saved meanwhile, the
 * server archives nothing (`revision-changed`): the page asks it again and shows the confirmation
 * anew, saying the course changed. Done, the catalogue shows the course archived, and the card says
 * so (`onNotice`), since the button is gone with it.
 */
export function ArchiveAction({
  course,
  title,
  onNotice,
}: {
  course: CatalogCourse
  title: string
  onNotice: (notice: CardNotice | null) => void
}) {
  const { t } = useI18n()
  const trpc = useTRPC()
  const client = useTRPCClient()
  const queryClient = useQueryClient()
  const reauth = useReauth()
  const button = useRef<HTMLButtonElement>(null)
  const [reach, setReach] = useState<{
    companies: number
    people: number
    draft: SeenDraft | null
  } | null>(null)
  const [open, setOpen] = useState(false)
  /** The server refused the confirmation's archive: the course changed since it was worked out. */
  const [changed, setChanged] = useState(false)
  const confirmed = useRef(false)

  const refused = (err: unknown) => {
    if (sessionGone(err)) return
    const reason = errorReason(err)
    if (reason === 'revision-changed') {
      // A draft opened or saved since the confirmation: asked again, and confirmed anew.
      setChanged(true)
      check.mutate()
    } else if (
      reason === 'course-archived' ||
      reason === 'not-published' ||
      reason === 'not-found'
    ) {
      onNotice({ tone: 'error', text: t('catalog.archive.unavailable') })
      // Someone else changed it meanwhile: the catalogue is asked again.
      void queryClient.invalidateQueries({ queryKey: trpc.catalog.list.queryKey() })
    } else onNotice({ tone: 'error', text: refusalText(t, err) })
  }

  const check = useMutation({
    mutationFn: () => client.catalog.archiveCheck.query({ courseId: course.id }),
    gcTime: 0,
    onSuccess: (r) => {
      setReach(r)
      setOpen(true)
    },
    onError: refused,
  })

  const archive = useMutation({
    mutationFn: (draft: SeenDraft | null) =>
      reauth.run(() => client.catalog.archive.mutate({ courseId: course.id, draft }), {
        from: button.current,
      }),
    gcTime: 0,
    onSuccess: (r) => {
      // Cancelled at the passkey: nothing done, nothing to say.
      if (!r) return
      queryClient.setQueryData(
        trpc.catalog.list.queryKey(),
        (d) =>
          d && {
            courses: d.courses.map((c) =>
              c.id === course.id ? { ...c, status: 'archived' as const } : c,
            ),
          },
      )
      onNotice({ tone: 'done', text: t('catalog.archive.done', { course: title }) })
    },
    onError: refused,
  })

  const busy = check.isPending || archive.isPending
  return (
    <>
      <Button
        ref={button}
        variant="danger"
        size="sm"
        aria-label={t('catalog.archive.label', { course: title })}
        aria-disabled={busy || undefined}
        className="aria-disabled:cursor-progress aria-disabled:opacity-50"
        onClick={() => {
          if (busy) return
          onNotice(null)
          setChanged(false)
          check.mutate()
        }}
      >
        <Archive aria-hidden className="size-4" />
        {check.isPending
          ? t('catalog.archive.checking')
          : archive.isPending
            ? t('catalog.archive.busy')
            : t('catalog.archive')}
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title={t('catalog.archive.title', { course: title })}
        confirmLabel={t('catalog.archive')}
        cancelLabel={t('common.cancel')}
        danger
        onConfirm={() => {
          confirmed.current = true
          setOpen(false)
        }}
        opener={button}
        onClosed={() => {
          // Once the focus is back on the button: the passkey's dialog may take it.
          if (!confirmed.current || !reach) return
          confirmed.current = false
          archive.mutate(reach.draft)
        }}
      >
        {changed && <p>{t('catalog.archive.changed')}</p>}
        <p>{t('catalog.archive.body')}</p>
        {reach && (
          <ul className="list-disc space-y-1 pl-5">
            <li>{t('catalog.archive.companies', { count: reach.companies })}</li>
            <li>{t('catalog.archive.people', { count: reach.people })}</li>
            {reach.draft && <li>{t('catalog.archive.draft')}</li>}
          </ul>
        )}
        <PasskeyNote />
      </ConfirmDialog>
    </>
  )
}
